import { ArrowDown } from 'lucide-react';
import type { JourneyGraph } from '@ederest/contracts';
import { Badge } from '../../../shared/ui/Badge.js';
import { FrictionScore, NOTABLE_EXIT_RATE } from '../../../shared/ui/FrictionScore.js';
import './journey-flow.css';

interface JourneyFlowProps {
  graph: JourneyGraph;
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
}

/** Stroke of the busiest passage, in pixels; the others are drawn in proportion. */
const MAX_STROKE_PX = 10;
const MIN_STROKE_PX = 2;

const count = (value: number) => value.toLocaleString('fr-FR');
const percent = (ratio: number) => `${Math.round(ratio * 100)} %`;

/**
 * The journey as it is walked: its pages in order, the visitors who go from
 * one to the next on the arrow between them, and those who leave the portal
 * falling away under the page they left from. Read straight from the graph
 * the API returns; a click on a page opens its measurement.
 */
export function JourneyFlow({ graph, selectedStepId, onSelectStep }: JourneyFlowProps) {
  const steps = [...graph.steps].sort((a, b) => a.position - b.position);
  if (steps.length === 0) return <p className="flow__empty">Aucune page mesurée sur ce parcours.</p>;

  const reference = Math.max(1, ...steps.map((step) => step.sessionCount));
  const passage = (fromId: string, toId: string) =>
    graph.transitions.find((transition) => transition.fromStepId === fromId && transition.toStepId === toId)
      ?.sessionCount ?? 0;

  return (
    <ol className="flow" aria-label="Pages du parcours, dans l'ordre">
      {steps.map((step, index) => {
        const next = steps[index + 1];
        const isLast = !next;
        const exited = isLast ? 0 : Math.round(step.sessionCount * step.dropOffRate);
        const moved = next ? passage(step.id, next.id) : 0;
        const share = step.sessionCount === 0 ? 0 : moved / step.sessionCount;
        const guidances = graph.guidances.filter((guidance) => guidance.stepId === step.id);
        const live = guidances.filter((guidance) => guidance.state === 'published').length;
        const proposed = guidances.filter(
          (guidance) => guidance.state === 'proposed' || guidance.state === 'approved',
        ).length;

        return (
          <li key={step.id} className="flow__item">
            <div className="flow__column">
              <button
                type="button"
                className={`flow__page${step.id === selectedStepId ? ' flow__page--selected' : ''}`}
                onClick={() => onSelectStep(step.id)}
                aria-pressed={step.id === selectedStepId}
              >
                <span className="flow__head">
                  <span className="flow__index">{step.position}</span>
                  <span className="flow__name">{step.name}</span>
                </span>
                <span className="flow__path">{step.pagePathPattern}</span>
                <span className="flow__entered">
                  <strong>{count(step.sessionCount)}</strong> usagers
                </span>
                <FrictionScore score={step.frictionScore} />
                {(live > 0 || proposed > 0) && (
                  <span className="flow__chips">
                    {live > 0 && <Badge tone="positive">{live} en ligne</Badge>}
                    {proposed > 0 && (
                      <Badge tone="brand">
                        {proposed} proposée{proposed > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </span>
                )}
              </button>

              {steps.length === 1 && (
                <p className="flow__note">Seule page mesurée : l'usager y fait toute sa démarche.</p>
              )}
              {exited > 0 && (
                <p
                  className={`flow__exit${step.dropOffRate >= NOTABLE_EXIT_RATE ? ' flow__exit--alert' : ''}`}
                >
                  <ArrowDown size={14} aria-hidden="true" />
                  <span>
                    <strong>{count(exited)}</strong> ont quitté le portail · {percent(step.dropOffRate)}
                  </span>
                </p>
              )}
            </div>

            {next && (
              <div className="flow__link" aria-label={`${count(moved)} usagers passent à la page suivante`}>
                <span className="flow__link-label">
                  <strong>{count(moved)}</strong> usagers · {percent(share)}
                </span>
                <span className="flow__arrow" aria-hidden="true">
                  <span
                    className="flow__arrow-line"
                    style={{
                      height: `${Math.max(MIN_STROKE_PX, Math.round((moved / reference) * MAX_STROKE_PX))}px`,
                    }}
                  />
                  <span className="flow__arrow-head" />
                </span>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
